import React, { useState } from "react";
import PropTypes from "prop-types";
import Joi from "joi";

// Redux
import { connect } from "react-redux";
import genericAction from "@Actions/generic";

// Lodash
import { replace, map, isEmpty } from "lodash";

// Components
import IKModal from "@Components/IkModal";
import Input from "@Components/Input";

// Utils
import { phoneFormat } from "@Utils/formats";

// Styles
import styles from "./styles.module.css";

const ModalContactCreate = ({ open, onClose, onSave, dispatch, isEmail, fixedFields = [] }) => {
	const getEmailConfig = () => {
		return (
			fixedFields.find((field) => {
				return field.label === "email";
			}) || { isRequired: false, isEditable: true }
		);
	};

	const emailConfig = getEmailConfig();

	const buildSchema = () => {
		const schemaFields = {
			name: Joi.string().min(2).required().messages({
				"string.min": "Nombre debe tener mínimo 2 caracteres",
				"string.empty": "Nombre es un campo requerido",
				"any.required": "Nombre es un campo requerido",
			}),
			phone: Joi.string().pattern(/^1/).required().messages({
				"string.pattern.base": "El teléfono debe comenzar con el número 1",
				"string.empty": "Teléfono es un campo requerido",
				"any.required": "Teléfono es un campo requerido",
			}),
		};

		schemaFields.email = emailConfig.isRequired
			? Joi.string()
					.email({ tlds: { allow: false } })
					.required()
					.messages({
						"string.email": "Correo no válido",
						"string.empty": "Email es un campo requerido",
						"any.required": "Email es un campo requerido",
					})
			: Joi.string()
					.email({ tlds: { allow: false } })
					.allow("")
					.messages({
						"string.email": "Correo no válido",
					});

		return Joi.object().keys(schemaFields);
	};

	const schema = buildSchema();

	const [form, setForm] = useState({ name: "", phone: "", email: "" });
	const [errors, setErrors] = useState({ name: "", phone: "", email: "" });

	const handleSave = async () => {
		const cleanedPhone = replace(form.phone, /\D/g, "");
		const validationData = {
			...form,
			phone: cleanedPhone,
		};

		const validation = schema.validate(validationData, { abortEarly: false });

		if (validation.error) {
			const { details } = validation.error;
			let errorsVal = { name: "", phone: "", email: "" };
			map(details, (item) => {
				errorsVal = { ...errorsVal, [item.context.key]: item.message };
			});
			setErrors(errorsVal);
			return;
		}

		if (errors.phone || errors.email || errors.name) {
			return;
		}

		await dispatch(
			genericAction({
				method: "post",
				route: "contacts",
				body: {
					...form,
					phone: cleanedPhone,
					isEmail,
				},
				onSuccess: (response) => {
					onSave?.(response.data);
					setForm({ name: "", phone: "", email: "" });
					setErrors({ name: "", phone: "", email: "" });
					onClose();
				},
			})
		);
	};

	const handlePhoneCopy = (e) => {
		e.preventDefault();
		const cleanedPhone = replace(form.phone, /\D/g, "");
		navigator.clipboard.writeText(cleanedPhone);
	};

	const handleInputChange = (e, { name, value }) => {
		setForm((prevForm) => {
			return { ...prevForm, [name]: value };
		});
		setErrors((prevErrors) => {
			return { ...prevErrors, [name]: "" };
		});
	};

	return (
		<IKModal
			open={open}
			title="Crear Contacto"
			onClose={onClose}
			onSuccess={handleSave}
			saveLabel="Guardar"
			cancelLabel="Cancelar"
			onCancel={onClose}
			size="tiny">
			<div className={styles.container}>
				<Input
					label={
						<>
							Nombre: <span className={styles.required}>*</span>
						</>
					}
					name="name"
					placeholder="Ingresa el nombre"
					value={form.name}
					onChange={handleInputChange}
					required
					error={!isEmpty(errors.name) && { content: errors.name, pointing: "above", className: styles.errorInput }}
					className={styles.inputLabel}
				/>
				<Input
					label={
						<>
							Teléfono: <span className={styles.required}>*</span>
						</>
					}
					name="phone"
					placeholder="Ingresa el teléfono"
					value={phoneFormat(form.phone)}
					onChange={handleInputChange}
					onCopy={handlePhoneCopy}
					required
					error={!isEmpty(errors.phone) && { content: errors.phone, pointing: "above", className: styles.errorInput }}
					className={styles.inputLabel}
				/>
				<Input
					label={<>Email: {emailConfig.isRequired && <span className={styles.required}>*</span>}</>}
					name="email"
					placeholder="Ingresa el email"
					value={form.email}
					onChange={handleInputChange}
					required={emailConfig.isRequired}
					error={!isEmpty(errors.email) && { content: errors.email, pointing: "above", className: styles.errorInput }}
					className={styles.inputLabel}
				/>
			</div>
		</IKModal>
	);
};

ModalContactCreate.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func,
	onSave: PropTypes.func,
	dispatch: PropTypes.func,
	isEmail: PropTypes.bool,
	fixedFields: PropTypes.array,
};

export default connect()(ModalContactCreate);
